import { createApiBffClient, createGainBffClient } from '@/services/api';
import { LayerName } from '@etp/etp-pipelines-client/axios';
import { ActionContext, Module } from 'vuex';
import { PipelinesStoreModule, PipelinesStoreState } from '@/store/pipelines';
import {
  ApiFileConfiguration,
  GainImportConfiguration,
  ProcessType
} from '@etp/etp-gain-bff-client/axios';
import { ImportFilesDto } from '@etp/etp-bff-client/axios';

const getDefaultState = () => {
  return {
    ...new PipelinesStoreModule(LayerName.ImportLayer).state,
    gainImportConfiguration: {} as GainImportConfiguration,
    gainImportConfigurationHistory: new Array<GainImportConfiguration>(),
    gainImportApiFileConfigurations: new Array<ApiFileConfiguration>(),
    gainImportApiFileConfigurationHistory: new Array<ApiFileConfiguration>(),
    selectedProcessType: ProcessType.GainData
  };
};

interface ImportState extends PipelinesStoreState {
  gainImportConfiguration: GainImportConfiguration;
  gainImportConfigurationHistory: Array<GainImportConfiguration>;
  gainImportApiFileConfigurations: Array<ApiFileConfiguration>;
  gainImportApiFileConfigurationHistory: Array<ApiFileConfiguration>;
  selectedProcessType: ProcessType;
}

const pipelinesStore = new PipelinesStoreModule(LayerName.ImportLayer);

class ImportModule implements Module<ImportState, any> {
  namespaced = true;
  state = {
    ...pipelinesStore.state,
    gainImportConfiguration: {} as GainImportConfiguration,
    gainImportConfigurationHistory: new Array<GainImportConfiguration>(),
    gainImportApiFileConfigurations: new Array<ApiFileConfiguration>(),
    gainImportApiFileConfigurationHistory: new Array<ApiFileConfiguration>(),
    selectedProcessType: ProcessType.GainData
  };
  getters = {
    ...pipelinesStore.getters,
    getGainImportConfiguration: (state: ImportState) =>
      state.gainImportConfiguration,
    getGainImportConfigurationHistory: (state: ImportState) =>
      state.gainImportConfigurationHistory,
    getGainImportApiFileConfigurations: (state: ImportState) =>
      state.gainImportApiFileConfigurations,
    getGainImportApiFileConfigurationHistory: (state: ImportState) =>
      state.gainImportApiFileConfigurationHistory,
    getSelectedProcessType: (state: ImportState) => state.selectedProcessType
  };
  actions = {
    ...pipelinesStore.actions,
    async importFiles(
      context: ActionContext<ImportState, any>,
      payload: {
        storagePurpose: string;
        path: Array<string>;
        fileNames: Array<string>;
      }
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      let client = createApiBffClient();

      await client.importFilesToDb(
        engagementId,
        payload.storagePurpose,
        payload.path.join('/'),
        {
          fileNames: payload.fileNames
        } as ImportFilesDto
      );
    },
    async fetchGainImportConfiguration(
      context: ActionContext<ImportState, any>
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      const { data, status } =
        await createGainBffClient().getLatestImportProcessConfiguration(
          engagementId,
          context.state.selectedProcessType
        );
      if (status == 200) {
        context.commit('SET_GAIN_IMPORT_CONFIGURATION', data);
      } else {
        context.commit('SET_GAIN_IMPORT_CONFIGURATION', {});
      }
    },
    async fetchGainImportConfigurationHistory(
      context: ActionContext<ImportState, any>
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      const { data, status } =
        await createGainBffClient().listImportProcessConfigurations(
          engagementId,
          context.state.selectedProcessType
        );
      if (status == 200) {
        context.commit('SET_GAIN_IMPORT_CONFIGURATION_HISTORY', data);
      } else {
        context.commit(
          'SET_GAIN_IMPORT_CONFIGURATION_HISTORY',
          new Array<GainImportConfiguration>()
        );
      }
    },
    async fetchGainImportApiFileConfigurations(
      context: ActionContext<ImportState, any>
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      const { data, status } =
        await createGainBffClient().listLatestApiFileConfigurations(
          engagementId
        );
      if (status == 200) {
        context.commit('SET_GAIN_IMPORT_API_FILE_CONFIGURATION', data);
      } else {
        context.commit(
          'SET_GAIN_IMPORT_API_FILE_CONFIGURATION',
          new Array<ApiFileConfiguration>()
        );
      }
    },
    async updateGainImportConfiguration(
      context: ActionContext<ImportState, any>,
      payload: GainImportConfiguration
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      await createGainBffClient().setImportProcessConfiguration(
        engagementId,
        context.state.selectedProcessType,
        payload
      );
    },

    async setSelectedProcessType(
      context: ActionContext<ImportState, any>,
      payload: { processType: ProcessType }
    ) {
      context.commit('SET_SELECTED_PROCESS_TYPE', payload.processType);
    },
    async fetchGainImportApiFileConfigurationHistory(
      context: ActionContext<ImportState, any>,
      configurationName: string
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      const { data, status } =
        await createGainBffClient().listApiFileConfigurations(
          engagementId,
          configurationName
        );
      if (status == 200) {
        context.commit('SET_GAIN_IMPORT_API_FILE_CONFIGURATION_HISTORY', data);
      } else {
        context.commit(
          'SET_GAIN_IMPORT_API_FILE_CONFIGURATION_HISTORY',
          new Array<ApiFileConfiguration>()
        );
      }
    },
    async createOrUpdateGainImportApiFileConfiguration(
      context: ActionContext<ImportState, any>,
      payload: ApiFileConfiguration
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      if (!payload.configurationName) return;
      await createGainBffClient().setApiFileConfiguration(
        engagementId,
        payload.configurationName,
        payload
      );
    },
    async recalculateApiNextRuns(context: ActionContext<ImportState, any>) {
      let engagementId = context.rootState.home.currentEngagement.id;
      await createGainBffClient().refreshApiNextRunConfigurations(engagementId);
    },
    async deleteGainImportApiFileConfiguration(
      context: ActionContext<ImportState, any>,
      configurationName: string
    ) {
      let engagementId = context.rootState.home.currentEngagement.id;
      await createGainBffClient().deleteApiFileConfigurations(
        engagementId,
        configurationName
      );
    }
  };
  mutations = {
    ...pipelinesStore.mutations,
    SET_GAIN_IMPORT_CONFIGURATION(
      state: ImportState,
      payload: GainImportConfiguration
    ) {
      state.gainImportConfiguration = payload;
    },
    SET_GAIN_IMPORT_API_FILE_CONFIGURATION(
      state: ImportState,
      payload: Array<ApiFileConfiguration>
    ) {
      state.gainImportApiFileConfigurations = payload;
    },
    SET_GAIN_IMPORT_CONFIGURATION_HISTORY(
      state: ImportState,
      payload: Array<GainImportConfiguration>
    ) {
      state.gainImportConfigurationHistory = payload;
    },
    SET_GAIN_IMPORT_API_FILE_CONFIGURATION_HISTORY(
      state: ImportState,
      payload: Array<ApiFileConfiguration>
    ) {
      state.gainImportApiFileConfigurationHistory = payload;
    },
    SET_SELECTED_PROCESS_TYPE(state: ImportState, payload: ProcessType) {
      state.selectedProcessType = payload;
    },
    RESET_STATE(state: ImportState) {
      Object.assign(state, getDefaultState());
    }
  };
}

export default new ImportModule();
